import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { FaqDialogResult, SpuFaqDialogComponent } from '../spu-faq-dialog/spu-faq-dialog.component';
import { FaqEditItem } from './spu-faq.model';

const FAQ_TABLE_COLUMNS: TableColumn[] = [
  { label: 'goods.spu.form.faq.question_vi', prop: 'questionVi', type: 'text' },
  { label: 'goods.spu.form.faq.answer_vi', prop: 'answerVi', type: 'text' },
  { label: 'goods.spu.form.faq.question_en', prop: 'questionEn', type: 'text' },
  { label: 'goods.spu.form.faq.answer_en', prop: 'answerEn', type: 'text' },
  { label: 'goods.spu.table.columns.status', prop: 'active', type: 'status' },
  { label: 'common.table.columns.actions', prop: 'actions', type: 'actions' },
];

@Component({
  selector: 'app-spu-faq-editor',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    AppButtonComponent,
    DataTableComponent,
    SpuFaqDialogComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './spu-faq-editor.component.html',
  styleUrl: './spu-faq-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpuFaqEditorComponent {
  private readonly translateService = inject(TranslateService);

  @Input() items: FaqEditItem[] = [];
  @Input() viewMode = false;
  @Output() itemsChange = new EventEmitter<FaqEditItem[]>();

  readonly columns = FAQ_TABLE_COLUMNS;

  dialogVisible = signal(false);
  editingItem = signal<FaqEditItem | null>(null);
  selectedItems = signal<FaqEditItem[]>([]);

  private newTempIdCounter = 0;

  openAddDialog(): void {
    this.editingItem.set(null);
    this.dialogVisible.set(true);
  }

  openEditDialog(item: FaqEditItem): void {
    this.editingItem.set(item);
    this.dialogVisible.set(true);
  }

  onDialogConfirm(result: FaqDialogResult): void {
    const editing = this.editingItem();
    if (editing) {
      this.itemsChange.emit(
        this.items.map((item) =>
          item.tempId === editing.tempId ? { ...item, ...result } : item,
        ),
      );
      return;
    }

    const newItem: FaqEditItem = {
      tempId: `new-faq-${++this.newTempIdCounter}`,
      sortOrder: this.items.length,
      active: true,
      ...result,
    };
    this.itemsChange.emit([...this.items, newItem]);
  }

  /** Row status toggle and delete both confirm first — mirrors Product Management's
   *  activate/deactivate/delete confirm flow (ordinary-spu.component.ts onEnable/
   *  onDisable/onDelete), reusing the same button-label i18n keys. */
  private pendingAction: 'toggle-status' | 'delete-item' | 'delete-selected' | null =
    null;
  private togglingItem: FaqEditItem | null = null;
  private deletingTempId: string | null = null;
  confirmDialogVisible = signal(false);
  confirmDialogMessage = signal('');
  confirmDialogButtons = signal<ConfirmationButton[]>([]);

  requestToggleStatus(item: FaqEditItem): void {
    this.pendingAction = 'toggle-status';
    this.togglingItem = item;
    const activating = !item.active;
    this.confirmDialogMessage.set(
      activating
        ? 'goods.spu.form.faq.confirm_activate'
        : 'goods.spu.form.faq.confirm_deactivate',
    );
    this.confirmDialogButtons.set([
      { label: 'common.actions.cancel', action: 'cancel', outlined: true },
      {
        label: activating
          ? 'goods.spu.actions.activate'
          : 'goods.spu.actions.deactivate',
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmDialogVisible.set(true);
  }

  requestDeleteItem(tempId: string): void {
    this.pendingAction = 'delete-item';
    this.deletingTempId = tempId;
    this.confirmDialogMessage.set('goods.spu.form.faq.confirm_delete');
    this.confirmDialogButtons.set(this.deleteConfirmButtons());
    this.confirmDialogVisible.set(true);
  }

  requestDeleteSelected(): void {
    if (this.selectedItems().length === 0) {
      return;
    }
    this.pendingAction = 'delete-selected';
    this.confirmDialogMessage.set(
      this.translateService.instant('goods.spu.form.faq.confirm_delete_selected', {
        count: this.selectedItems().length,
      }),
    );
    this.confirmDialogButtons.set(this.deleteConfirmButtons());
    this.confirmDialogVisible.set(true);
  }

  private deleteConfirmButtons(): ConfirmationButton[] {
    return [
      { label: 'common.actions.cancel', action: 'cancel', outlined: true },
      { label: 'common.actions.delete', action: 'confirm', severity: 'danger' },
    ];
  }

  onConfirmDialogAction(action: string): void {
    if (action === 'confirm') {
      switch (this.pendingAction) {
        case 'toggle-status':
          this.applyToggleStatus();
          break;
        case 'delete-item':
          this.applyDeleteItem();
          break;
        case 'delete-selected':
          this.applyDeleteSelected();
          break;
      }
    }
    this.pendingAction = null;
    this.togglingItem = null;
    this.deletingTempId = null;
    this.confirmDialogVisible.set(false);
  }

  private applyToggleStatus(): void {
    if (!this.togglingItem) {
      return;
    }
    const tempId = this.togglingItem.tempId;
    this.itemsChange.emit(
      this.items.map((item) =>
        item.tempId === tempId ? { ...item, active: !item.active } : item,
      ),
    );
  }

  private applyDeleteItem(): void {
    if (!this.deletingTempId) {
      return;
    }
    this.itemsChange.emit(
      this.items.filter((item) => item.tempId !== this.deletingTempId),
    );
  }

  private applyDeleteSelected(): void {
    const selectedTempIds = new Set(this.selectedItems().map((item) => item.tempId));
    if (selectedTempIds.size === 0) {
      return;
    }
    this.itemsChange.emit(
      this.items.filter((item) => !selectedTempIds.has(item.tempId)),
    );
    this.selectedItems.set([]);
  }

  moveItemUp(tempId: string): void {
    this.moveItem(tempId, -1);
  }

  moveItemDown(tempId: string): void {
    this.moveItem(tempId, 1);
  }

  isFirst(item: FaqEditItem): boolean {
    return this.items[0]?.tempId === item.tempId;
  }

  isLast(item: FaqEditItem): boolean {
    return this.items[this.items.length - 1]?.tempId === item.tempId;
  }

  private moveItem(tempId: string, offset: number): void {
    const items = this.items;
    const index = items.findIndex((item) => item.tempId === tempId);
    const targetIndex = index + offset;
    if (index === -1 || targetIndex < 0 || targetIndex >= items.length) {
      return;
    }
    const reordered = [...items];
    [reordered[index], reordered[targetIndex]] = [
      reordered[targetIndex],
      reordered[index],
    ];
    this.itemsChange.emit(reordered);
  }
}
